import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DepartureBoard from '../components/DepartureBoard';
import StationPicker from '../components/StationPicker';
import { useFavorites } from '../hooks/useFavorites';
import { Star, ChevronRight, Search } from 'lucide-react';

const Tableau = () => {
  const [params] = useSearchParams();
  const station = params.get('gare');
  const navigate = useNavigate();
  const { favorites } = useFavorites();
  const { t } = useTranslation();
  const [isLowVision, setIsLowVision] = useState(
    document.body.classList.contains('a11y-lowvision')
  );
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsLowVision(document.body.classList.contains('a11y-lowvision'));
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 overflow-x-hidden">
      <nav className="flex items-center gap-2 text-sm text-slate-500 mb-4">
        <span className="hover:text-ter-blue cursor-pointer" onClick={() => navigate('/')}>{t('nav.home')}</span>
        <ChevronRight className="w-3.5 h-3.5" />
        <span className="font-semibold text-ter-blue">{t('nav.board')}</span>
        {station && (
          <>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="font-semibold text-slate-700">{station}</span>
          </>
        )}
      </nav>

      {!station ? (
        <div className="grid xl:grid-cols-5 gap-6 items-start" data-testid="tableau-empty-state">
          <div className="xl:col-span-3 bg-white rounded-2xl shadow-sm border border-slate-200 p-8 sm:p-10">
            <div className="w-14 h-14 rounded-xl bg-ter-yellow/20 flex items-center justify-center mb-4">
              <Search className="w-6 h-6 text-ter-blue" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-ter-blue leading-tight">
              {t('tableau.empty_title')}
            </h1>
            <p className="text-slate-600 mt-2 text-sm">{t('tableau.empty_text')}</p>
            <div className="mt-6">
              <StationPicker />
            </div>
          </div>

          {favorites.length > 0 && (
            <div className="xl:col-span-2 bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
                <Star className="w-4 h-4 fill-ter-yellow text-ter-yellow" />
                <h3 className="font-extrabold text-ter-blue">{t('picker.favorites_title')}</h3>
              </div>
              <ul className="divide-y divide-slate-100">
                {favorites.map((name) => (
                  <li key={name}>
                    <button onClick={() => navigate(`/tableau?gare=${encodeURIComponent(name)}`)} className="w-full text-left px-5 py-3 flex items-center justify-between hover:bg-slate-50">
                      <span className="font-semibold text-slate-900">{name}</span>
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : (
        <div className={`grid gap-6 ${isLowVision ? 'grid-cols-1' : 'xl:grid-cols-3'}`}>
          <div className={`space-y-6 min-w-0 ${isLowVision ? '' : 'xl:col-span-2'}`}>
            <DepartureBoard stationName={station} stopAreaIdProp={params.get('id')} />
          </div>
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">{t('picker.another_station')}</div>
              <StationPicker />
            </div>

            {favorites.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
                  <Star className="w-4 h-4 fill-ter-yellow text-ter-yellow" />
                  <h3 className="font-extrabold text-ter-blue">{t('picker.favorites_title')}</h3>
                </div>
                <ul className="divide-y divide-slate-100">
                  {favorites.map((name) => (
                    <li key={name}>
                      <button onClick={() => navigate(`/tableau?gare=${encodeURIComponent(name)}`)} className="w-full text-left px-5 py-3 flex items-center justify-between hover:bg-slate-50">
                        <span className="font-semibold text-slate-900">{name}</span>
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Tableau;
